"use client";

import { useNavigate } from "react-router-dom";
import { BASE_PRICING, perGeneration } from "@/config/pricing";
import { ROUTES } from "@/config/routes";
import { useLocationPricing } from "../hooks/useLocationPricing";

const PAID_PLANS = [
  { name: "Starter", ...BASE_PRICING.starter, best: false },
  { name: "Plus", ...BASE_PRICING.plus, best: false },
  { name: "Pro", ...BASE_PRICING.pro, best: true },
];

// The value of the plans: resume generations and AI section edits are
// separate credits. Numbers come from the pricing config.
export default function ValueSection() {
  const navigate = useNavigate();
  // Prices in the visitor's currency: rupees in India, dollars elsewhere.
  const { isIndianUser } = useLocationPricing();
  const price = (plan: (typeof PAID_PLANS)[number]) =>
    isIndianUser ? `₹${plan.price.inr}` : `$${plan.price.usd}`;
  // Both credits count: resume generations and AI section edits.
  const each = (plan: (typeof PAID_PLANS)[number]) => {
    const total = plan.generations + plan.sectionEdits;
    return isIndianUser
      ? perGeneration(plan.price.inr, total, "₹")
      : perGeneration(plan.price.usd, total, "$");
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">More AI for your money</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-10">
          Resumes and AI section edits are separate credits, so fine-tuning never uses up a resume.
          One-time payments, no subscription.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {PAID_PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`rounded-2xl bg-white p-6 shadow-sm border ${
                plan.best ? "border-purple-300 ring-2 ring-purple-100" : "border-gray-100"
              }`}
            >
              <p className="font-semibold text-gray-900">{plan.name}</p>
              {/* Hidden until the currency is known, so prices don't flip. */}
              <p className={`mt-1 text-3xl font-bold text-gray-900 ${isIndianUser === null ? "invisible" : ""}`}>
                {price(plan)}
              </p>
              <p className="mt-3 text-sm text-gray-700">
                {plan.generations} AI resumes + {plan.sectionEdits} AI section credits
              </p>
              <p className="text-sm text-gray-700">
                + {plan.profiles} saved profile{plan.profiles > 1 ? "s" : ""}
              </p>
              <p
                className={`mt-3 inline-block rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700 ${
                  isIndianUser === null ? "invisible" : ""
                }`}
              >
                about {each(plan)} per AI generation
              </p>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => navigate(ROUTES.PRICING)}
          className="mt-8 text-sm font-medium text-blue-600 underline"
        >
          Compare all plans, including Free
        </button>
      </div>
    </section>
  );
}
